import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, ClipboardList, ListTodo, RefreshCw, Search, UserPlus, Wallet } from 'lucide-react';

import { ROUTE_PATHS } from '../../app/router/route-paths';
import { fetchClientsBookings } from '../../features/bookings/api/bookings.api';
import type { StaffSlotBookingRow } from '../../features/bookings/api/bookings.types';
import { fetchScheduleSlots } from '../../features/schedule-slots/api/schedule-slots.api';
import type { ScheduleSlotListRow } from '../../features/schedule-slots/api/schedule-slots.types';
import { fetchTaskBoard } from '../../features/tasks/api/tasks.api';
import { useOrgScheduleTimezone } from '../../features/weekly-calendar/hooks/useOrgScheduleTimezone';
import { todayYmd } from '../../features/weekly-calendar/utils/timeUtils';
import { hasPermission, isModuleEnabled, isRouteAllowed } from '../../shared/access/access';
import { useAuth } from '../../shared/auth/use-auth';
import { userFacingLoadError } from '../../shared/lib/staff-user-facing-error';
import { Alert } from '../../shared/ui/Alert';
import { Button, getButtonClassNames } from '../../shared/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/Card';
import { Spinner } from '../../shared/ui/Spinner';
import { summarizeTodaySlots, isUnpaidBooking } from './dashboard-today';

/**
 * Назначение: блок «Сегодня» вверху дашборда — занятия дня с заполненностью, неоплаченные записи,
 * задачи на мне и быстрые действия.
 * Кто: администратор организации (каждый показатель — только при доступе к своему разделу).
 * Вход: главная страница CRM в контексте организации.
 */
export function DashboardTodayCard({ organizationId, branchId }: { organizationId: number; branchId: number | null }) {
  const { apiClient, me } = useAuth();
  const caps = me?.capabilities ?? null;
  const canSchedule = isRouteAllowed(caps, '/schedule');
  const canBookings = isRouteAllowed(caps, '/clients-bookings');
  const canTasks = isModuleEnabled(caps, 'tasks') && hasPermission(caps, 'tasks:read') && isRouteAllowed(caps, '/tasks');
  const canClients = isRouteAllowed(caps, '/clients');

  const timezone = useOrgScheduleTimezone({ apiClient, organizationId, branchId });
  const today = todayYmd(timezone);

  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slots, setSlots] = useState<ScheduleSlotListRow[]>([]);
  const [bookings, setBookings] = useState<StaffSlotBookingRow[]>([]);
  const [myTasks, setMyTasks] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [slotRes, bookingRes, boardRes] = await Promise.allSettled([
        canSchedule
          ? fetchScheduleSlots(apiClient, { organization_id: organizationId, from_date: today, to_date: today, branch_id: branchId })
          : Promise.resolve([] as ScheduleSlotListRow[]),
        canBookings
          ? fetchClientsBookings(apiClient, { from_date: today, to_date: today, limit: 200, branch_id: branchId ?? undefined })
          : Promise.resolve({ items: [] as StaffSlotBookingRow[], total: 0 }),
        canTasks ? fetchTaskBoard(apiClient) : Promise.resolve(null)
      ]);

      const failed = [slotRes, bookingRes].find((result) => result.status === 'rejected');
      if (failed && failed.status === 'rejected') {
        setError(userFacingLoadError(failed.reason));
        return;
      }

      if (slotRes.status === 'fulfilled') setSlots(slotRes.value);
      if (bookingRes.status === 'fulfilled') setBookings(bookingRes.value.items);
      if (boardRes.status === 'fulfilled' && boardRes.value) {
        setMyTasks(me?.id != null ? boardRes.value.cards.filter((c) => c.assignee_user_id === me.id).length : null);
      }
      setHasLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [apiClient, branchId, canBookings, canSchedule, canTasks, me?.id, organizationId, today]);

  useEffect(() => {
    void load();
  }, [load]);

  const initialLoading = loading && !hasLoaded;
  const showContent = !initialLoading && (!error || hasLoaded);

  const summary = useMemo(() => summarizeTodaySlots(slots, new Date(), timezone), [slots, timezone]);
  const unpaid = useMemo(() => bookings.filter((b) => b.status !== 'cancelled' && isUnpaidBooking(b)).length, [bookings]);
  const activeBookings = useMemo(() => bookings.filter((b) => b.status !== 'cancelled').length, [bookings]);

  const stat = 'rounded-lg border border-[hsl(var(--ui-border))] p-3';
  const statLabel = 'staff-m-0 flex items-center gap-1.5 text-xs font-medium text-[hsl(var(--ui-muted-foreground))]';
  const statValue = 'staff-m-0 mt-1 text-2xl font-semibold tabular-nums text-[hsl(var(--ui-foreground))]';

  return (
    <Card data-testid="dashboard-today-card">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
        <div>
          <CardTitle className="text-base">Сегодня</CardTitle>
          <CardDescription>Занятия, записи и задачи на сегодня.</CardDescription>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="inline-flex shrink-0 items-center gap-1.5"
          onClick={() => void load()}
          disabled={loading}
          aria-busy={loading}
          data-testid="dashboard-today-refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
          {loading ? 'Обновление…' : 'Обновить'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {initialLoading ? <Spinner label="Загрузка…" /> : null}
        {!initialLoading && error && !hasLoaded ? (
          <div data-testid="dashboard-today-error">
            <Alert variant="error" title="Не удалось загрузить данные">
              <p className="staff-m-0 text-sm">{error}</p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="mt-2 inline-flex items-center gap-1.5"
                onClick={() => void load()}
                disabled={loading}
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                Повторить
              </Button>
            </Alert>
          </div>
        ) : null}
        {!initialLoading && error && hasLoaded ? (
          <div data-testid="dashboard-today-refresh-error">
            <Alert variant="warning" title="Не удалось обновить данные">
              <p className="staff-m-0 text-sm">{error}</p>
              <p className="staff-m-0 mt-1 text-xs">Показаны ранее загруженные данные.</p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="mt-2 inline-flex items-center gap-1.5"
                onClick={() => void load()}
                disabled={loading}
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                Повторить
              </Button>
            </Alert>
          </div>
        ) : null}
        {showContent ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {canSchedule ? (
              <Link to={ROUTE_PATHS.schedule} className={`${stat} hover:bg-[hsl(var(--ui-muted))]`}>
                <p className={statLabel}>
                  <CalendarDays className="h-3.5 w-3.5" aria-hidden /> Занятий сегодня
                </p>
                <p className={statValue}>{summary.total}</p>
                <p className="staff-m-0 text-xs text-[hsl(var(--ui-muted-foreground))]">
                  {summary.total > 0 ? `Заполнено мест: ${summary.taken} из ${summary.capacity}` : 'Занятий нет'}
                </p>
              </Link>
            ) : null}
            {canBookings ? (
              <Link to={ROUTE_PATHS.clientsBookings} className={`${stat} hover:bg-[hsl(var(--ui-muted))]`}>
                <p className={statLabel}>
                  <ClipboardList className="h-3.5 w-3.5" aria-hidden /> Записей на сегодня
                </p>
                <p className={statValue}>{activeBookings}</p>
              </Link>
            ) : null}
            {canBookings ? (
              <Link to={ROUTE_PATHS.clientsBookings} className={`${stat} hover:bg-[hsl(var(--ui-muted))]`}>
                <p className={statLabel}>
                  <Wallet className="h-3.5 w-3.5" aria-hidden /> Без оплаты
                </p>
                <p className={`${statValue} ${unpaid > 0 ? 'text-[hsl(var(--ui-warning))]' : ''}`}>{unpaid}</p>
                <p className="staff-m-0 text-xs text-[hsl(var(--ui-muted-foreground))]">записей на сегодня</p>
              </Link>
            ) : null}
            {canTasks && myTasks != null ? (
              <Link to={ROUTE_PATHS.tasks} className={`${stat} hover:bg-[hsl(var(--ui-muted))]`}>
                <p className={statLabel}>
                  <ListTodo className="h-3.5 w-3.5" aria-hidden /> Задачи на мне
                </p>
                <p className={statValue}>{myTasks}</p>
              </Link>
            ) : null}
          </div>
        ) : null}

        {showContent && summary.upcoming.length > 0 ? (
          <ul className="space-y-1.5" aria-label="Ближайшие занятия">
            {summary.upcoming.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="min-w-0 truncate">
                  <span className="font-medium tabular-nums">{s.start_time.slice(0, 5)}</span> · {s.service_name}
                  {s.trainer_name ? <span className="text-[hsl(var(--ui-muted-foreground))]"> · {s.trainer_name}</span> : null}
                  {s.status === 'cancelled' ? (
                    <span className="font-medium text-[hsl(var(--ui-destructive))]"> · Отменено</span>
                  ) : null}
                </span>
                <span className="text-xs tabular-nums text-[hsl(var(--ui-muted-foreground))]">
                  {s.status === 'cancelled'
                    ? 'Отменено'
                    : `${Math.max(0, s.max_participants - s.places_left)} / ${s.max_participants}`}
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {canSchedule ? (
            <Link to={ROUTE_PATHS.schedule} className={getButtonClassNames({ variant: 'primary', size: 'sm', className: 'inline-flex items-center gap-1.5' })}>
              <UserPlus className="h-4 w-4" aria-hidden /> Записать клиента
            </Link>
          ) : null}
          {canClients ? (
            <Link to={ROUTE_PATHS.tenantUsersClientsTab} className={getButtonClassNames({ variant: 'secondary', size: 'sm', className: 'inline-flex items-center gap-1.5' })}>
              <Search className="h-4 w-4" aria-hidden /> Найти клиента
            </Link>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
